import {
  assetConfigurations,
  AssetManagementPRMApplication,
  CompactTable,
  ConfigProvider,
  defaultResponseHandler,
  FieldAsset,
  insertObjIf,
  Page,
  PermissionGuard,
  useApiFilters,
  useUserinfo,
} from '@mss/mss-component-library';
import React, { useMemo, useState } from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import PageSidebar from 'jsx/common/FilterSidebar';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import RemoteConnectModal from '../../../../asset/components/RemoteConnectModal';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import Button from '../../../../common/Button';
import Fetch from '../../../../common/util/_fetch';
import MoreActionsMenu from '../../../components/MoreActionMenu';
import { sanitizedUrl } from '../../../util/sanitize';
import columns from './columns';

const PAGE_SIZE = 10;

const permissions = [
  'view_fieldassetprm',
  'add_fieldassetprm',
  'change_fieldassetprm',
  'delete_fieldassetprm',
];

export default function List() {
  const [t] = useTranslation(['asset', 'common']);
  const [, , , hasAnyPermission] = useUserinfo();
  const history = useHistory();
  const { url } = useRouteMatch();
  const [connectModal, openConnectModal] = useState<string | null>(null);

  const [count, setCount] = useState(0);
  const filterApi = useApiFilters(t, count, PAGE_SIZE);

  const assetManagementApps = useQuery({
    queryKey: ['autocomplete', 'assetManagementApps'],
    queryFn: async () => {
      const resp = await defaultResponseHandler<
        AssetManagementPRMApplication[]
      >({
        t,
        request: Fetch.get(
          '/api/assets/autocomplete/name/?model_name=AssetManagementApplicationPRM'
        ),
      });
      return resp.data.reduce(
        (assetApp, current) => ({
          ...assetApp,
          [current.id!]: current,
        }),
        {}
      );
    },
    enabled: hasAnyPermission(permissions),
  });

  const prmAssets = useQuery({
    queryKey: ['fieldAssets', String(filterApi.urlParams)],
    queryFn: async () => {
      const resp = await defaultResponseHandler<{
        count: number;
        results: FieldAsset[];
      }>({
        t,
        request: Fetch.get(
          `/api/assets/?type=field&deleted=false${filterApi.urlParams ? `&${filterApi.urlParams}` : ''}`
        ),
      });
      return {
        ...resp.data,
        results: resp.data.results.map(x => ({ ...x, field_type: 'fieldprm' })),
      };
    },
    enabled: !!filterApi.urlParams && assetManagementApps.isSuccess,
    onSuccess: data => setCount(data.count),
  });

  const _columns = columns({
    openConnectModal,
    t,
    assetManagementApps: assetManagementApps.data ?? {},
  });

  const filters = useQuery({
    queryKey: ['fieldAssets', 'filters'],
    queryFn: async () => {
      const resp = await defaultResponseHandler<any[]>({
        t,
        request: Fetch.get('/api/assets/filters/?type=field&deleted=false'),
      });
      return resp.data;
    },
    enabled: assetManagementApps.isSuccess,
    onSuccess: data => filterApi.updateFilters(data as any),
  });

  const availableFilters = useMemo(
    () =>
      _columns
        .map(x => x.key)
        .reduce(
          (acc, key) => ({
            ...acc,
            ...insertObjIf(
              key in filterApi.filters &&
                filterApi.filters[key].options.length > 1,
              {
                [key]: filterApi.filters[key],
              }
            ),
          }),
          {}
        ),
    [filterApi.filters, _columns]
  );

  return (
    <ConfigProvider<FieldAsset> value={assetConfigurations['field']}>
      <PermissionGuard
        any
        codename={[
          'view_fieldassetprm',
          'add_fieldassetprm',
          'change_fieldassetprm',
          'delete_fieldassetprm',
        ]}
      >
        <Page
          breadCrumbs={<Breadcrumbs />}
          padding={false}
          actions={
            <>
              <PermissionGuard codename={'view_fieldassetdiscovery'}>
                <Button
                  onClick={() => history.push(`/assets/field/discovery`)}
                  data-ta="newAssetButton"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t('Field Asset Discovery')}
                </Button>
              </PermissionGuard>
              <MoreActionsMenu
                count={prmAssets.data?.results?.length}
                type="field"
              />
            </>
          }
          main={
            <PermissionGuard any codename={['view_fieldassetprm']}>
              <CompactTable
                bordered={false}
                dataSource={prmAssets.data?.results}
                loading={prmAssets.isLoading}
                columns={_columns}
                rowKey={'id'}
                pagination={filterApi.pagination}
                onChange={filterApi.onChangeAssetTable}
                onRow={(record: any) => {
                  return {
                    onClick: () =>
                      history.push(
                        `${sanitizedUrl(url)}/${record.field_type}/${
                          record.id
                        }/asset-settings`
                      ),
                  };
                }}
              />
              <RemoteConnectModal
                closeModal={() => openConnectModal(null)}
                asset={
                  prmAssets.data?.results.find(
                    (x: any) => x.id === connectModal
                  )! as any
                }
                visible={!!connectModal}
              />
            </PermissionGuard>
          }
          sidebar={
            <PageSidebar
              loading={filters.isFetching}
              availableFilters={availableFilters}
              toggleFilter={filterApi.toggleFilter}
              search={{
                value: filterApi.search,
                set: filterApi.setSearch,
              }}
              selectedFilters={filterApi.selectedFilters}
              translationPrefix={['asset']}
            />
          }
        />
      </PermissionGuard>
    </ConfigProvider>
  );
}
